// src/components/crm/PropertyEditorModal.js
// PROPERTIES v1 — PropertyEditorModal — 5e0a94c7
//
// Self-contained editor for a single customer "property" (additional service
// location). Collects an optional name + optional contact, and a fully
// address-verified location. Verification mirrors the customer address flow in
// CustomerFormModal.js (same validateAddress CF, same status handling). This is
// a deliberate scoped copy — the customer verification code is mature/stable, so
// a second copy carries little divergence risk. If it ever needs to change in
// both places, fold the shared helpers into utils/address.js at that time.
//
// The modal does NOT write to Firestore itself — it builds a complete property
// object (including the five verify fields) and hands it to onSave(property).
// The parent (CustomerFormModal) owns the array write, following the existing
// per-tab save pattern. Note: array elements cannot hold serverTimestamp(), so
// addressVerifiedAt is a client Timestamp here (not the server one the main
// customer address uses).

import React, { useState, useEffect, useRef } from 'react';
import { functions } from '../../firebase/config';
import { Timestamp } from 'firebase/firestore';
import { X, CheckCircle, AlertCircle, AlertTriangle, Loader2, MapPin, Save } from 'lucide-react';
import { formatPhoneNumber } from '../../utils/formatters';
import { countries, statesDetailed } from '../../utils/data';
import { validateAddressViaCF } from '../../utils/address';

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers (copied faithfully from CustomerFormModal address verification).
// ─────────────────────────────────────────────────────────────────────────────
const CA_POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;

const ORDERED_COUNTRIES = (() => {
    const top = [];
    const rest = [];
    countries.forEach(c => {
        if (c.code === 'CA' || c.code === 'US') top.push(c);
        else rest.push(c);
    });
    top.sort((a, b) => (a.code === 'CA' ? -1 : 1));
    return { top, rest };
})();

const formatPostalOnBlur = (value, country) => {
    if (!value) return '';
    const cleaned = value.toString().trim().toUpperCase();
    if (country === 'CA') {
        const compact = cleaned.replace(/[\s-]/g, '');
        if (compact.length === 6) return `${compact.slice(0, 3)} ${compact.slice(3)}`;
        return cleaned;
    }
    if (country === 'US') {
        const digits = cleaned.replace(/\D/g, '');
        if (digits.length === 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
        if (digits.length === 5) return digits;
        return cleaned;
    }
    return cleaned;
};

const validateAddressFormat = (data) => {
    const errors = {};
    const country = data.country;
    if (country === 'CA' || country === 'US') {
        const validList = (statesDetailed[country] || []).map(s => s.code);
        if (data.state && !validList.includes(data.state)) {
            errors.state = country === 'CA'
                ? 'Please select a valid province or territory.'
                : 'Please select a valid state.';
        }
    }
    if (data.zip) {
        const zipTrim = data.zip.toString().trim();
        if (country === 'CA' && !CA_POSTAL_RE.test(zipTrim)) {
            errors.zip = 'Postal code must be in format A1A 1A1.';
        } else if (country === 'US' && !US_ZIP_RE.test(zipTrim)) {
            errors.zip = 'ZIP must be 12345 or 12345-6789.';
        }
    }
    return errors;
};

const detectCanonicalDifferences = (typed, canonical) => {
    if (!canonical || typeof canonical !== 'string') return [];
    const diffs = [];
    const canonLower = canonical.toLowerCase();

    // Postal code
    const typedZip = (typed.zip || '').trim();
    const caPostalMatch = canonical.match(/[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/);
    const usPostalMatch = canonical.match(/\b\d{5}(-\d{4})?\b/);
    const googlePostal = caPostalMatch ? caPostalMatch[0] : (usPostalMatch ? usPostalMatch[0] : null);
    if (googlePostal) {
        if (!typedZip) {
            diffs.push({ field: 'zip', label: 'Postal code', typed: '(empty)', canonical: googlePostal });
        } else {
            const typedZipCompact = typedZip.replace(/\s/g, '').toLowerCase();
            const canonHasZip = canonLower.replace(/\s/g, '').includes(typedZipCompact);
            if (!canonHasZip && googlePostal.toLowerCase() !== typedZip.toLowerCase()) {
                diffs.push({ field: 'zip', label: 'Postal code', typed: typedZip, canonical: googlePostal });
            }
        }
    }

    // Street number
    const typedAddr1 = (typed.address1 || '').trim();
    const typedNumMatch = typedAddr1.match(/^(\d+\w?)\s/);
    if (typedNumMatch) {
        const typedNum = typedNumMatch[1].toLowerCase();
        const canonNumMatch = canonical.match(/^(\d+\w?)\s/);
        const canonNum = canonNumMatch ? canonNumMatch[1].toLowerCase() : null;
        if (canonNum && canonNum !== typedNum) {
            diffs.push({ field: 'address1', label: 'Street number', typed: typedNumMatch[1], canonical: canonNumMatch[1] });
        } else if (!canonNum) {
            diffs.push({ field: 'address1', label: 'Street number', typed: typedNumMatch[1], canonical: '(not found — street only)', removeNumber: true });
        }
    }

    // City
    const typedCity = (typed.city || '').trim();
    if (typedCity && !canonLower.includes(typedCity.toLowerCase())) {
        const parts = canonical.split(',').map(p => p.trim());
        const firstHasNumber = /^\d+\w?\s/.test(parts[0] || '');
        const googleCity = firstHasNumber
            ? (parts.length >= 3 ? parts[1] : null)
            : (parts.length >= 2 ? parts[0] : null);
        if (googleCity && googleCity.toLowerCase() !== typedCity.toLowerCase()) {
            diffs.push({ field: 'city', label: 'City', typed: typedCity, canonical: googleCity });
        }
    }

    return diffs;
};

const newPropertyId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `prop_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const ADDRESS_FIELDS = ['address1', 'address2', 'city', 'state', 'zip', 'country'];

const PropertyEditorModal = ({ editingProperty = null, defaultCountry = 'CA', onClose, onSave, customers = [], currentCustomerId = null }) => { // billto-ped-props-2c9e4f71
    const [form, setForm] = useState(() => ({
        name: editingProperty?.name || '',
        contactName: editingProperty?.contactName || '',
        contactPhone: editingProperty?.contactPhone || '',
        contactEmail: editingProperty?.contactEmail || '',
        address1: editingProperty?.address1 || '',
        address2: editingProperty?.address2 || '',
        city: editingProperty?.city || '',
        state: editingProperty?.state || '',
        zip: editingProperty?.zip || '',
        country: editingProperty?.country || defaultCountry,
    }));
    // billto-ped-state-8e2a6c94 -- per-property default bill-to: a POINTER (mode + optional
    // third-party customer id), never a snapshot. The snapshot is built at document-creation
    // time by the quote/job/invoice modals via src/utils/billTo.js. Absent = main customer.
    const [defaultBillTo, setDefaultBillTo] = useState(() => ({
        mode: editingProperty?.defaultBillTo?.mode || 'main',
        thirdPartyCustomerId: editingProperty?.defaultBillTo?.thirdPartyCustomerId || null,
    }));

    const [addressErrors, setAddressErrors] = useState({});
    const [geocode, setGeocode] = useState(() => {
        // If editing an already-verified property, show the verified state up front.
        if (editingProperty?.addressVerified && editingProperty?.addressCanonical) {
            return {
                status: 'ok',
                canonical: editingProperty.addressCanonical,
                coords: editingProperty.addressCoords || null,
                placeId: editingProperty.addressPlaceId || null,
                alternatives: null, message: null, differences: null,
            };
        }
        return { status: 'idle', canonical: null, coords: null, placeId: null, alternatives: null, message: null, differences: null };
    });
    const [saving, setSaving] = useState(false);

    const verifyTimerRef = useRef(null);
    const idRef = useRef(editingProperty?.id || newPropertyId());

    // Close on Escape.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Cleanup the debounce timer on unmount.
    useEffect(() => () => {
        if (verifyTimerRef.current) { clearTimeout(verifyTimerRef.current); verifyTimerRef.current = null; }
    }, []);

    // ── Verification machine ──────────────────────────────────────────────────
    const runVerification = async (snapshot) => {
        if (!snapshot.address1?.trim() || !snapshot.city?.trim()) return;
        setGeocode({ status: 'checking', canonical: null, coords: null, placeId: null, alternatives: null, message: null, differences: null });
        try {
            const result = await validateAddressViaCF(functions, {
                address1: snapshot.address1, address2: snapshot.address2,
                city: snapshot.city, state: snapshot.state,
                zip: snapshot.zip, country: snapshot.country,
            });
            if (result.status === 'ok') {
                const hasFullPostal = /[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d|\b\d{5}(-\d{4})?\b/.test(result.canonical);
                const fsaOnlyMatch = !hasFullPostal && result.canonical.match(/\b[A-Za-z]\d[A-Za-z]\b/);
                const googleFsa = fsaOnlyMatch ? fsaOnlyMatch[0].toUpperCase() : null;
                if (googleFsa) {
                    const typedFsa = (snapshot.zip || '').replace(/\s/g, '').slice(0, 3).toUpperCase();
                    if (typedFsa !== googleFsa) {
                        setGeocode({
                            status: 'not_found', canonical: null, coords: null, placeId: null, alternatives: null, differences: null,
                            message: `Google found this address but couldn't confirm a complete postal code (only the ${googleFsa} area). Please double-check your postal code, or save as-is.`,
                        });
                        return;
                    }
                }
                const diffs = detectCanonicalDifferences(snapshot, result.canonical);
                if (diffs.length > 0) {
                    setGeocode({ status: 'ok_with_changes', canonical: result.canonical, coords: result.coords, placeId: result.placeId, alternatives: null, differences: diffs, message: null });
                } else {
                    setGeocode({ status: 'ok', canonical: result.canonical, coords: result.coords, placeId: result.placeId, alternatives: null, differences: null, message: null });
                }
            } else if (result.status === 'ambiguous') {
                setGeocode({ status: 'ambiguous', canonical: null, coords: null, placeId: null, alternatives: result.alternatives || [], differences: null, message: null });
            } else if (result.status === 'not_found') {
                setGeocode({ status: 'not_found', canonical: null, coords: null, placeId: null, alternatives: null, differences: null, message: "We couldn't find this address. You can still save it." });
            } else {
                setGeocode({ status: 'error', canonical: null, coords: null, placeId: null, alternatives: null, differences: null, message: 'Verification temporarily unavailable.' });
            }
        } catch (err) {
            const code = err?.code || '';
            const message = code === 'functions/resource-exhausted'
                ? 'Address verification rate limit reached. You can still save.'
                : 'Verification temporarily unavailable.';
            setGeocode({ status: 'error', canonical: null, coords: null, placeId: null, alternatives: null, differences: null, message });
        }
    };

    const scheduleVerification = (snapshot) => {
        if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
        verifyTimerRef.current = setTimeout(() => {
            verifyTimerRef.current = null;
            runVerification(snapshot);
        }, 300);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (ADDRESS_FIELDS.includes(name) && geocode.status !== 'idle') {
            setGeocode({ status: 'idle', canonical: null, coords: null, placeId: null, alternatives: null, message: null, differences: null });
        }
        if (name === 'contactPhone') {
            const digits = value.replace(/\D/g, '').slice(0, 10);
            setForm(p => ({ ...p, contactPhone: formatPhoneNumber(digits) }));
        } else {
            setForm(p => ({ ...p, [name]: value }));
        }
    };

    const handleAddressFieldBlur = () => scheduleVerification(form);

    const handlePostalBlur = () => {
        const formatted = formatPostalOnBlur(form.zip, form.country);
        const updated = { ...form, zip: formatted };
        setForm(updated);
        setAddressErrors(p => ({ ...p, zip: undefined }));
        scheduleVerification(updated);
    };

    const handleCountryChange = (newCountry) => {
        const hasStateOrZip = (form.state && form.state.trim()) || (form.zip && form.zip.trim());
        if (hasStateOrZip) {
            const ok = window.confirm('Changing the country will clear the Province/State and Postal/ZIP code fields. Continue?');
            if (!ok) return;
        }
        setForm(p => ({ ...p, country: newCountry, state: '', zip: '' }));
        setAddressErrors({});
        setGeocode({ status: 'idle', canonical: null, coords: null, placeId: null, alternatives: null, message: null, differences: null });
    };

    const acceptCanonicalChanges = (diffs) => {
        const updates = {};
        diffs.forEach(d => {
            if (d.field === 'zip') updates.zip = d.canonical;
            if (d.field === 'city') updates.city = d.canonical;
            if (d.field === 'address1') {
                const rest = (form.address1 || '').replace(/^(\d+\w?)\s/, '');
                updates.address1 = d.removeNumber ? rest.trim() : `${d.canonical} ${rest}`.trim();
            }
        });
        setForm(prev => ({ ...prev, ...updates }));
        setGeocode(prev => ({ ...prev, status: 'ok', differences: null }));
    };

    const keepTypedAddress = () => setGeocode(prev => ({ ...prev, status: 'ok', differences: null }));

    const handlePickAddressAlternative = (alt) => {
        const diffs = detectCanonicalDifferences(form, alt.canonical);
        if (diffs.length > 0) {
            const updates = {};
            diffs.forEach(d => {
                if (d.field === 'zip') updates.zip = d.canonical;
                if (d.field === 'city') updates.city = d.canonical;
                if (d.field === 'address1') {
                    const rest = (form.address1 || '').replace(/^(\d+\w?)\s/, '');
                    updates.address1 = d.removeNumber ? rest.trim() : `${d.canonical} ${rest}`.trim();
                }
            });
            setForm(prev => ({ ...prev, ...updates }));
        }
        setGeocode({ status: 'ok', canonical: alt.canonical, coords: alt.coords, placeId: alt.placeId, alternatives: null, differences: null, message: null });
    };

    // ── Build + save ──────────────────────────────────────────────────────────
    const buildProperty = () => {
        const base = {
            id: idRef.current,
            name: (form.name || '').trim(),
            contactName: (form.contactName || '').trim(),
            contactPhone: (form.contactPhone || '').trim(),
            contactEmail: (form.contactEmail || '').trim(),
            address1: (form.address1 || '').trim(),
            address2: (form.address2 || '').trim(),
            city: (form.city || '').trim(),
            state: form.state || '',
            zip: form.zip || '',
            country: form.country || defaultCountry,
            // billto-ped-build-3f7b9d25 -- a thirdParty default with no customer picked falls back to main.
            defaultBillTo: (defaultBillTo.mode === 'thirdParty' && !defaultBillTo.thirdPartyCustomerId)
                ? { mode: 'main', thirdPartyCustomerId: null }
                : { mode: defaultBillTo.mode, thirdPartyCustomerId: defaultBillTo.mode === 'thirdParty' ? defaultBillTo.thirdPartyCustomerId : null },
        };
        if (geocode.status === 'ok' || geocode.status === 'ok_with_changes') {
            base.addressVerified = true;
            base.addressVerifiedAt = Timestamp.now();
            base.addressCanonical = geocode.canonical || null;
            base.addressCoords = geocode.coords || null;
            base.addressPlaceId = geocode.placeId || null;
        } else if (geocode.status === 'not_found' || geocode.status === 'ambiguous') {
            base.addressVerified = false;
            base.addressVerifiedAt = Timestamp.now();
            base.addressCanonical = null;
            base.addressCoords = null;
            base.addressPlaceId = null;
        } else {
            // idle / checking / error → preserve prior verification when editing.
            base.addressVerified = editingProperty?.addressVerified || false;
            base.addressVerifiedAt = editingProperty?.addressVerifiedAt || Timestamp.now();
            base.addressCanonical = editingProperty?.addressCanonical || null;
            base.addressCoords = editingProperty?.addressCoords || null;
            base.addressPlaceId = editingProperty?.addressPlaceId || null;
        }
        return base;
    };

    const handleSaveClick = async () => {
        const errs = validateAddressFormat(form);
        if (Object.keys(errs).length > 0) {
            setAddressErrors(errs);
            alert(`Please fix the following before saving:\n\n${Object.values(errs).join('\n')}`);
            return;
        }
        if (!form.address1.trim() || !form.city.trim()) {
            alert('A property needs at least a street address and a city.');
            return;
        }
        setSaving(true);
        try {
            await onSave(buildProperty());
            // Parent closes the modal on success.
        } catch (err) {
            console.error('Property save failed:', err);
            alert('Failed to save the property. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const isEditing = !!editingProperty;

    return (
        <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <MapPin size={18} className="text-blue-600" />
                        <h3 className="text-base font-bold text-gray-800">{isEditing ? 'Edit property' : 'Add property'}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                    {/* Name */}
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Property name (optional)</label>
                        <input
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder='e.g. "Maple Court Plaza" or "123 Rental — Unit B"'
                            className="mt-1 p-2 border rounded text-sm w-full min-w-0"
                        />
                    </div>

                    {/* Address */}
                    <div className="space-y-3">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Address</h4>
                        <input name="address1" placeholder="Address Line 1" value={form.address1} onChange={handleChange} onBlur={handleAddressFieldBlur} className="p-2 border rounded text-sm w-full min-w-0" />
                        <input name="address2" placeholder="Address Line 2 (optional)" value={form.address2 || ''} onChange={handleChange} onBlur={handleAddressFieldBlur} className="p-2 border rounded text-sm w-full min-w-0" />
                        <input name="city" placeholder="City" value={form.city} onChange={handleChange} onBlur={handleAddressFieldBlur} className="p-2 border rounded text-sm w-full min-w-0" />
                        <select
                            name="country"
                            value={form.country || defaultCountry}
                            onChange={(e) => handleCountryChange(e.target.value)}
                            className="p-2 border rounded text-sm w-full min-w-0 bg-white"
                        >
                            {ORDERED_COUNTRIES.top.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                            <option disabled>──────────</option>
                            {ORDERED_COUNTRIES.rest.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                        <div className="grid grid-cols-2 gap-2">
                            {(form.country === 'CA' || form.country === 'US') ? (
                                <select
                                    name="state"
                                    value={form.state || ''}
                                    onChange={handleChange}
                                    className={`p-2 border rounded text-sm w-full min-w-0 bg-white ${addressErrors.state ? 'border-red-500' : ''}`}
                                >
                                    <option value="">{form.country === 'CA' ? '-- Province / Territory --' : '-- State --'}</option>
                                    {(statesDetailed[form.country] || []).map(s => (
                                        <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    name="state"
                                    placeholder="Region/State/Province"
                                    value={form.state || ''}
                                    onChange={handleChange}
                                    className="p-2 border rounded text-sm w-full min-w-0"
                                />
                            )}
                            <input
                                name="zip"
                                placeholder={form.country === 'CA' ? 'A1A 1A1' : (form.country === 'US' ? '12345' : 'Postal Code')}
                                value={form.zip}
                                onChange={handleChange}
                                onBlur={handlePostalBlur}
                                maxLength={20}
                                className={`p-2 border rounded text-sm w-full min-w-0 ${addressErrors.zip ? 'border-red-500' : ''}`}
                            />
                        </div>
                        {addressErrors.state && <p className="text-xs text-red-600 mt-1">{addressErrors.state}</p>}
                        {addressErrors.zip && <p className="text-xs text-red-600 mt-1">{addressErrors.zip}</p>}

                        {/* Geocode verification status row */}
                        {geocode.status === 'checking' && (
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                <Loader2 size={12} className="animate-spin" />
                                <span>Verifying address…</span>
                            </div>
                        )}
                        {geocode.status === 'ok' && (
                            <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2 mt-1">
                                <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <div className="font-semibold">Address verified</div>
                                    {geocode.canonical && (
                                        <div className="text-green-600 mt-0.5 flex items-start gap-1">
                                            <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                            <span className="break-words">{geocode.canonical}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {geocode.status === 'ok_with_changes' && geocode.differences && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1 space-y-2">
                                <div className="flex items-start gap-2 text-xs text-amber-800">
                                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold">Google adjusted your address</div>
                                        <div className="mt-1 space-y-0.5">
                                            {geocode.differences.map((d, idx) => (
                                                <div key={idx} className="text-amber-700">
                                                    <span className="font-medium">{d.label}:</span>{' '}
                                                    <span className="line-through text-amber-500">{d.typed}</span>
                                                    {' → '}
                                                    <span className="font-semibold">{d.canonical}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {geocode.canonical && (
                                            <div className="mt-1.5 text-amber-600 flex items-start gap-1">
                                                <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                                <span className="break-words">{geocode.canonical}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => acceptCanonicalChanges(geocode.differences)} className="flex-1 px-2 py-1 bg-amber-600 text-white text-xs font-semibold rounded hover:bg-amber-700 transition-colors">Accept changes</button>
                                    <button type="button" onClick={keepTypedAddress} className="flex-1 px-2 py-1 bg-white border border-amber-300 text-amber-700 text-xs font-semibold rounded hover:bg-amber-100 transition-colors">Use what I typed</button>
                                </div>
                            </div>
                        )}
                        {geocode.status === 'ambiguous' && geocode.alternatives?.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1 space-y-2">
                                <div className="flex items-center gap-2 text-xs text-amber-800 font-semibold">
                                    <AlertTriangle size={13} />
                                    <span>Did you mean one of these?</span>
                                </div>
                                <div className="space-y-1">
                                    {geocode.alternatives.map((alt, idx) => (
                                        <button key={idx} type="button" onClick={() => handlePickAddressAlternative(alt)} className="w-full text-left text-xs px-2 py-1.5 bg-white border border-amber-200 rounded hover:border-amber-400 hover:bg-amber-50 flex items-start gap-2">
                                            <MapPin size={11} className="flex-shrink-0 mt-0.5 text-amber-600" />
                                            <span className="break-words flex-1">{alt.canonical}</span>
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[11px] text-amber-700">Or save as-is — we'll flag it as unverified.</p>
                            </div>
                        )}
                        {geocode.status === 'not_found' && (
                            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-1">
                                <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <div className="font-semibold">Address not verified</div>
                                    <div className="text-red-600 mt-0.5">{geocode.message}</div>
                                </div>
                            </div>
                        )}
                        {geocode.status === 'error' && (
                            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                                <span>{geocode.message}</span>
                            </div>
                        )}
                    </div>

                    {/* Contact */}
                    <div className="space-y-3">
                        <div>
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Site contact (optional)</h4>
                            <p className="text-[11px] text-gray-400 mt-0.5">Leave any field blank to use the main customer contact.</p>
                        </div>
                        <input name="contactName" placeholder="Contact name" value={form.contactName} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input name="contactPhone" placeholder="Contact phone" value={form.contactPhone} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                            <input name="contactEmail" type="email" placeholder="Contact email" value={form.contactEmail} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                        </div>
                    </div>

                    {/* billto-ped-ui-5b4e8a17 -- default bill-to for documents created for this property */}
                    <div className="space-y-3">
                        <div>
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Default bill-to (optional)</h4>
                            <p className="text-[11px] text-gray-400 mt-0.5">Pre-selects who gets billed on new quotes, jobs and invoices for this property. Always changeable on the document itself.</p>
                        </div>
                        <select
                            value={defaultBillTo.mode}
                            onChange={(e) => setDefaultBillTo(prev => ({ mode: e.target.value, thirdPartyCustomerId: e.target.value === 'thirdParty' ? prev.thirdPartyCustomerId : null }))}
                            className="p-2 border rounded text-sm w-full min-w-0 bg-white"
                        >
                            <option value="main">Main customer</option>
                            <option value="propertyContact">Property contact</option>
                            <option value="thirdParty">Another customer...</option>
                        </select>
                        {defaultBillTo.mode === 'thirdParty' && (
                            <select
                                value={defaultBillTo.thirdPartyCustomerId || ''}
                                onChange={(e) => setDefaultBillTo(prev => ({ ...prev, thirdPartyCustomerId: e.target.value || null }))}
                                className="p-2 border rounded text-sm w-full min-w-0 bg-white"
                            >
                                <option value="">-- Choose the customer to bill --</option>
                                {customers.filter(c => c.id !== currentCustomerId).map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 px-5 py-4 border-t flex-shrink-0">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded text-gray-600 font-medium hover:bg-gray-50">Cancel</button>
                    <button
                        type="button"
                        onClick={handleSaveClick}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 shadow-md flex items-center gap-2 disabled:opacity-60"
                    >
                        <Save size={16} />
                        {saving ? 'Saving…' : (isEditing ? 'Save property' : 'Add property')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PropertyEditorModal;